<!-- Invite — someone opened a friend's invite link. Activate if needed, then
     pair both ways. Screens role. -->
<template>
  <IonPage>
    <AppHeader title="Invite" back="/tabs/pager" />

    <IonContent :fullscreen="true" class="pm-scr-content">
      <div class="pm-scr-pad pm-scr-invite">
        <div class="pm-scr-lcd pm-scr-lcd--big" :class="glassClass" aria-live="polite">
          <p class="pm-scr-lcd-line">{{ lcdTop }}</p>
          <p v-if="lcdBottom" class="pm-scr-lcd-line pm-scr-lcd-line--s">{{ lcdBottom }}</p>
        </div>

        <!-- Ask -->
        <template v-if="state === 'ask'">
          <h2 class="screen-title">{{ owner ? `Pair with ${ownerLabel}?` : 'Pair with this pager?' }}</h2>
          <p class="screen-sub">
            {{ owner?.handle ? `${owner.handle} sent you their invite link.` : 'A friend sent you their invite link.' }}
            Pair, and you can page each other straight away.
            <template v-if="!ready"> You'll get your own pager number first — no sign‑up.</template>
          </p>
          <div class="pm-scr-stack">
            <IonButton expand="block" :disabled="pair.busy.value" @click="pair.run()">
              {{ pair.busy.value ? (ready ? 'Pairing…' : 'Getting your number…') : 'Pair' }}
            </IonButton>
            <IonButton expand="block" fill="outline" :disabled="pair.busy.value" @click="router.replace('/tabs/pager')">Not now</IonButton>
          </div>
        </template>

        <!-- Paired -->
        <template v-else-if="state === 'paired'">
          <h2 class="screen-title">You're paired</h2>
          <p class="screen-sub">You and {{ friendLabel }} can page each other now.</p>
          <!-- Save the number as someone, right here: prefilled with the name they chose. -->
          <form class="pm-scr-field" novalidate @submit.prevent="saveName.run()">
            <label class="pm-scr-label" :for="`${uid}-invite-name`">Save them as</label>
            <div class="pm-scr-inline">
              <input
                :id="`${uid}-invite-name`"
                v-model="nameText"
                class="pm-scr-input"
                type="text"
                maxlength="24"
                autocomplete="off"
                autocapitalize="words"
                :placeholder="owner?.handle || 'Name'"
              >
              <IonButton type="submit" fill="outline" :disabled="saveName.busy.value || savedName === nameText.trim()">Save</IonButton>
            </div>
            <p class="pm-scr-muted pm-scr-small">Only you see this name. {{ formatNumber(friendNumber) }}</p>
          </form>
          <div class="pm-scr-stack">
            <IonButton expand="block" @click="router.replace(`/tabs/compose/${friendNumber}`)">Send a page</IonButton>
            <IonButton expand="block" fill="outline" @click="router.replace('/tabs/pager')">Open pager</IonButton>
          </div>
        </template>

        <!-- Error -->
        <template v-else-if="state === 'error'">
          <h2 class="screen-title">{{ errorTitle }}</h2>
          <p class="screen-sub" role="alert">{{ errorText }}</p>
          <div class="pm-scr-stack">
            <IonButton v-if="friendNumber" expand="block" @click="router.replace(`/tabs/compose/${friendNumber}`)">Send a page</IonButton>
            <IonButton v-if="retryable" expand="block" :disabled="pair.busy.value" @click="pair.run()">Try again</IonButton>
            <IonButton expand="block" fill="outline" @click="router.replace('/tabs/friends')">Add a friend by number</IonButton>
            <IonButton expand="block" fill="clear" @click="router.replace('/tabs/pager')">Open pager</IonButton>
          </div>
        </template>

        <!-- The web pager, honestly. -->
        <!-- Not on a dead link: there is no "them" to page there. -->
        <section v-if="!isNative && state !== 'error'" class="pm-scr-notice pm-scr-install" aria-label="Get the app">
          <p>You can page them right here. Install the app to get beeped when they page back.</p>
          <div class="pm-scr-button-row">
            <IonButton fill="outline" href="https://apps.apple.com/app/id6815597780" target="_blank" rel="noopener">App Store</IonButton>
            <IonButton fill="outline" href="https://play.google.com/store/apps/details?id=com.harfredlabs.pageme" target="_blank" rel="noopener">Google Play</IonButton>
          </div>
        </section>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, computed, onMounted, useId } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButton } from '@ionic/vue';
import { AppHeader, useAction, toast } from '../core/ui/index.js';
import { formatNumber } from '../pm/format.js';
import { isNative } from '../core/native.js';
import {
  me, activation, settings, currentDevice, activate, acceptInvite, friendName, lookupInvite, renameFriend,
} from '../store.js';

const uid = `pm${useId()}`;

const props = defineProps({ code: { type: String, required: true } });
const router = useRouter();

const state = ref('ask'); // ask | paired | error
const friendNumber = ref('');
const errorKind = ref('');
const errorRaw = ref('');

const ready = computed(() => activation.value === 'ready' && !!me.value?.number);
const glassClass = computed(() => (settings.highContrast ? 'pm-scr-glass--hc' : `pm-scr-glass--${currentDevice.value?.lcd || 'classic'}`));
const friendLabel = computed(() => (friendNumber.value ? friendName(friendNumber.value) : 'your friend'));

// Whose link this is — looked up before pairing, so the question has a name.
const owner = ref(null);
const ownerLabel = computed(() => {
  if (!owner.value) return '';
  const num = formatNumber(owner.value.number);
  return owner.value.handle ? `${owner.value.handle} (${num})` : num;
});
onMounted(async () => {
  const r = await lookupInvite(props.code);
  if (r?.ok && r.number && r.number !== me.value?.number) owner.value = { number: r.number, handle: r.handle };
});

const lcdTop = computed(() => {
  if (pair.busy.value) return ready.value ? 'PAIRING…' : 'ACTIVATING…';
  if (state.value === 'paired') return `PAIRED WITH ${friendLabel.value.toUpperCase()}`;
  if (state.value === 'error') return errorKind.value === 'already' ? 'ALREADY PAIRED' : 'NO PAIR';
  return owner.value ? `INVITE FROM ${(owner.value.handle || formatNumber(owner.value.number)).toUpperCase()}` : 'INVITE RECEIVED';
});

const nameText = ref('');
const savedName = ref('');
const saveName = useAction(async () => {
  const typed = nameText.value.trim();
  const handle = owner.value?.handle || '';
  const r = renameFriend(friendNumber.value, handle && typed.toUpperCase() === handle.toUpperCase() ? '' : typed);
  if (!r.ok) throw new Error(r.message || "Couldn't save the name.");
  savedName.value = typed;
  toast(typed ? `Saved as ${typed}` : 'Name cleared');
});
const lcdBottom = computed(() => (state.value === 'ask' && !pair.busy.value ? 'PAIR?' : ''));

function classify(code) {
  const s = String(code || '').toLowerCase();
  if (s === 'self' || /own/.test(s)) return 'own';
  if (/already/.test(s)) return 'already';
  if (/not_found|invalid|bad_code|http_404|http_410/.test(s)) return 'bad';
  if (/offline|timeout|http_503/.test(s)) return 'offline';
  return 'other';
}

const errorTitle = computed(() => ({
  own: 'That\'s your own link',
  already: 'You\'re already friends',
  bad: 'This link doesn\'t work',
  offline: 'No connection',
  other: 'Couldn\'t pair',
}[errorKind.value] || 'Couldn\'t pair'));

const errorText = computed(() => ({
  own: 'This is your invite link. Send it to a friend, and when they open it you\'re paired.',
  already: 'You can already page each other.',
  bad: 'The link may be old — your friend can make a new one in Settings. Or add them by their pager number.',
  offline: 'Page Me needs a connection to pair. Try again when you\'re back online.',
  other: errorRaw.value || 'Something went wrong. Try again.',
}[errorKind.value] || errorRaw.value));

const retryable = computed(() => errorKind.value === 'offline' || errorKind.value === 'other');

function fail(r) {
  const number = r?.friend?.number;
  errorRaw.value = r?.message || '';
  errorKind.value = classify(r?.error);
  friendNumber.value = errorKind.value === 'already' ? (number || '') : '';
  state.value = 'error';
}

const pair = useAction(async () => {
  if (!ready.value) {
    const a = await activate();
    if (a && a.ok === false) { fail(a); return; }
    if (!me.value?.number) { fail({ error: 'offline' }); return; }
  }
  const r = await acceptInvite(props.code);
  if (!r?.ok) { fail(r); return; }
  friendNumber.value = r.friend?.number || '';
  nameText.value = r.friend?.name || r.friend?.handle || owner.value?.handle || '';
  savedName.value = r.friend?.name ? nameText.value : (r.friend?.handle || '');
  state.value = 'paired';
}, { weight: 'pop' });
</script>
